import { useEffect, useRef } from 'react'

/**
 * Size the app to the VISUAL viewport, not the window.
 *
 * This is the mechanism that stops the on-screen keyboard covering the terminal and the
 * composer. When the keyboard pops up the layout viewport does not change, but
 * `visualViewport.height` shrinks — so the app is driven from that instead:
 *
 *   --app-h   = visualViewport.height
 *   --app-top = visualViewport.offsetTop
 *
 * and `body` is `position: fixed` with `top: var(--app-top); height: var(--app-h)`.
 * Everything inside is anchored to the footer via --foot-total, so the whole stack
 * follows the keyboard up and back down.
 *
 * Ported from the original (index.html ~1124). Three details that are load-bearing:
 *  - Updates are rAF-coalesced: visualViewport fires resize AND scroll in bursts while
 *    the keyboard animates, and re-fitting xterm on each one is wasteful and janky.
 *  - `scroll` matters as much as `resize`: iOS shifts offsetTop when the keyboard
 *    appears without necessarily changing height.
 *  - `orientationchange` is re-synced after ~300ms, because the values read straight
 *    after the event are still the pre-rotation ones.
 *
 * It also publishes `body.kb-up` — see KEYBOARD_MIN_PX.
 */

/**
 * How much shorter the visual viewport must be before we call it a keyboard.
 *
 * The layout viewport does not shrink for the keyboard, so the difference between
 * `innerHeight` and `visualViewport.height` IS roughly the keyboard. But it is not
 * only the keyboard: Safari's collapsing URL bar moves it by up to ~100px with no
 * keyboard involved, and treating that as one would hide the top bar while the user
 * is merely scrolling. Phone keyboards are 250px and up, so 150px separates them
 * with room on both sides.
 */
const KEYBOARD_MIN_PX = 150

/**
 * Is the on-screen keyboard up?
 *
 * Two signals, because neither is sufficient alone.
 *
 * The viewport one is the honest measurement, but it assumes the WebView keeps its
 * full height and lets `visualViewport` report the difference — true in a browser,
 * NOT true under a Capacitor shell set to resize the WebView natively. There the
 * native frame shrinks, `innerHeight` shrinks with it, the difference is ~0, and a
 * check on that alone would silently never fire on the one platform this exists for.
 *
 * The focus one holds regardless of resize mode: on a touch device, a focused text
 * field means a keyboard. It is a proxy rather than a measurement, so it is the
 * fallback, not the primary — a hardware keyboard on an iPad would trip it, which
 * costs a hidden top bar and nothing else.
 */
function keyboardIsUp(): boolean {
  const vv = window.visualViewport
  if (vv && window.innerHeight - vv.height > KEYBOARD_MIN_PX) return true
  if (!matchMedia('(pointer: coarse)').matches) return false
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName
  return tag === 'TEXTAREA' || (tag === 'INPUT' && !/^(button|submit|checkbox|radio|file|range)$/i.test((el as HTMLInputElement).type))
}

/**
 * A frame this much shorter than its keyboard-down height counts as resized.
 * Small because the native resize measured 96px.
 */
const FRAME_SHRINK_MIN_PX = 20

/**
 * Is a native shell hosting this page?
 *
 * The frame correction below is only ever correct inside one, and the magnitude of
 * the shrink CANNOT decide that on its own: the native resize measured 96px and
 * Safari's collapsing URL bar moves `innerHeight` by up to about 100px with no
 * keyboard involved. The two ranges overlap, so no threshold separates them — and
 * getting it wrong in the browser inverts the layout, which is what shipping the
 * threshold alone did.
 *
 * Capacitor injects this object and a browser has no equivalent, so the check is on
 * the shell being PRESENT rather than on a platform being guessed. Absent it, this
 * module behaves exactly as it did before the correction existed.
 */
function inNativeShell(): boolean {
  return 'Capacitor' in window && !!(window as unknown as { Capacitor?: unknown }).Capacitor
}

export function useViewport(onResize?: () => void) {
  const cb = useRef(onResize)
  cb.current = onResize

  useEffect(() => {
    const vv = window.visualViewport
    const root = document.documentElement.style
    let pending = false

    /**
     * The frame's height with no keyboard up — the baseline the shrink is measured
     * against. Re-read whenever the keyboard is down, so browser chrome collapsing
     * or a window resize moves it rather than being mistaken for a keyboard.
     */
    let baseFrame = window.innerHeight

    const sync = () => {
      const kbUp = keyboardIsUp()
      if (!kbUp) baseFrame = window.innerHeight

      if (vv) {
        /**
         * Who has already accounted for the keyboard?
         *
         * In a browser, nobody: the layout viewport keeps its full height and
         * `visualViewport.height` is the only thing that shrinks, so it IS the
         * visible region. That is the model this file was built on.
         *
         * Under a Capacitor shell set to resize natively, BOTH happen — measured on
         * iOS 26.3 by avengents-app-builder: the frame goes 890 -> 794 and
         * visualViewport reports 570 at offsetTop 162. The app then renders 570 tall
         * inside a 794 frame and leaves 794-162-570 = 62px of frame empty at the
         * bottom, which is the black band under the keyboard the owner reported.
         * Two independent measurements put it at 62: that arithmetic, and pixels off
         * a photograph of a real phone.
         *
         * The frame bottom IS the keyboard top there — the band is visible, so the
         * frame cannot extend past it — which makes `innerHeight - offsetTop` the
         * visible height. In a browser that same expression is the keyboard height
         * too much, and would put a third of the app underneath it.
         *
         * "It cannot misfire in a browser, because there the frame does not shrink"
         * is what the first version of this said, and it was wrong. Safari's URL bar
         * collapses as you scroll and takes ~100px of `innerHeight` with it, no
         * keyboard involved — read as a shrink, that applies the native expression in
         * a browser and stands the layout on its head, composer above terminal. Which
         * is what the owner saw. A bigger threshold does not save it either: the
         * native shrink is 96px and the URL bar is up to ~100, so the magnitudes
         * overlap and nothing about the number separates them.
         *
         * Hence both conditions. The shell has to be there, AND the frame has to have
         * actually moved.
         */
        const frameShrunk = baseFrame - window.innerHeight > FRAME_SHRINK_MIN_PX
        const appH = frameShrunk && inNativeShell() ? window.innerHeight - vv.offsetTop : vv.height
        root.setProperty('--app-h', `${appH}px`)
        root.setProperty('--app-top', `${vv.offsetTop}px`)
      } else {
        // No visualViewport (older desktop browsers): the window is the viewport.
        root.setProperty('--app-h', `${window.innerHeight}px`)
        root.setProperty('--app-top', '0px')
      }
      document.body.classList.toggle('kb-up', kbUp)
      // After the class, never before: the callback re-fits xterm, and it has to
      // measure the layout the keyboard actually leaves behind.
      cb.current?.()
    }

    /**
     * Trailing re-sync after the keyboard finishes animating.
     *
     * The browser stops firing resize/scroll before the animation ends, so the last
     * rAF pass can capture an intermediate height — the app is then sized to a
     * keyboard that is still sliding, and the bottom rows stay hidden once it settles.
     * Re-reading a beat after the final event is what makes the end state correct;
     * it is cheap because it only runs when the burst has actually stopped.
     */
    let settle: number | undefined
    const scheduleSettle = () => {
      window.clearTimeout(settle)
      settle = window.setTimeout(sync, 250)
    }

    const schedule = () => {
      scheduleSettle()
      if (pending) return
      pending = true
      requestAnimationFrame(() => {
        pending = false
        sync()
      })
    }

    const onOrientation = () =>
      window.setTimeout(() => {
        /**
         * Rotating changes the frame's natural height, so a baseline taken in the
         * other orientation is meaningless — in a browser, rotating with the keyboard
         * up would read as a 400px "shrink" and size the app to the layout viewport,
         * putting it under the keyboard.
         *
         * Resetting unconditionally trades that for the opposite, milder failure:
         * rotate WITH the keyboard up on iOS and the band returns until the keyboard
         * is dismissed once. A cosmetic band beats hidden content, so the trade is
         * deliberate and this way round.
         */
        baseFrame = window.innerHeight
        sync()
      }, 300)

    window.addEventListener('resize', schedule)
    window.addEventListener('orientationchange', onOrientation)
    vv?.addEventListener('resize', schedule)
    vv?.addEventListener('scroll', schedule)
    // Focus moving in or out of a field changes the answer without changing any
    // size, so under a natively-resizing WebView these are the ONLY events that fire.
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', schedule)
    sync()

    return () => {
      window.clearTimeout(settle)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('orientationchange', onOrientation)
      vv?.removeEventListener('resize', schedule)
      vv?.removeEventListener('scroll', schedule)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', schedule)
    }
  }, [])
}
